import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X, UserCircle } from "@phosphor-icons/react";
import CurrencySwitcher from "./CurrencySwitcher";
import LangSwitcher from "./LangSwitcher";
import useActiveSection from "@/hooks/useActiveSection";
import { useI18n } from "@/context/LangContext";
import api from "@/lib/api";

const NAV_LINKS = [
  { href: "#services", id: "services", labelKey: "nav.services" },
  { href: "#products", id: "products", labelKey: "nav.products" },
  { href: "#erp", id: "erp", labelKey: "nav.erp" },
  { href: "#success", id: "success", labelKey: "nav.success" },
  { href: "#cases", id: "cases", labelKey: "nav.cases" },
  { href: "#insights", id: "insights", labelKey: "nav.insights" },
  { href: "/partners", id: "partners", labelKey: "nav.partners", external: true },
  { href: "#careers", id: "careers", labelKey: "nav.careers" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { hash, pathname } = useLocation();
  const { t } = useI18n();
  const isHome = pathname === "/";
  const to = (h) => isHome ? h : `/${h}`;
  const activeId = useActiveSection(NAV_LINKS.map((l) => l.id), { enabled: isHome });
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem("maxine_admin_token");
    if (!t) { setLoggedIn(false); return; }
    // Verify token is still valid; clear if it's not
    api.get("/auth/me")
      .then(() => setLoggedIn(true))
      .catch(() => {
        localStorage.removeItem("maxine_admin_token");
        localStorage.removeItem("maxine_admin_user");
        setLoggedIn(false);
      });
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [hash]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-200 ${
          scrolled ? "backdrop-blur-xl bg-white/85 border-b border-[color:var(--line)]" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img src="/logo-maxine.png" alt="Maxine Consulting OPC Private Limited" className="h-8 md:h-10 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm">
            {NAV_LINKS.map((l) => {
              const isActive = isHome && activeId === l.id;
              const linkTo = l.external ? l.href : (isHome ? l.href : `/${l.href}`);
              const label = t(l.labelKey);
              return (
                <Link key={l.href} to={linkTo}
                   data-testid={`nav-link-${l.id}`}
                   data-active={isActive ? "true" : undefined}
                   className={`relative py-1 transition-colors duration-200 font-medium after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:right-0 after:h-px after:bg-neutral-400 after:transition-opacity after:duration-200 ${
                     isActive
                       ? "text-[color:var(--brand)] font-semibold after:opacity-0"
                       : "text-neutral-700 hover:text-[color:var(--brand)] after:opacity-0 hover:after:opacity-100"
                   }`}>
                  {label}
                  {isActive && <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-[color:var(--brand)]" />}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <LangSwitcher />
            <CurrencySwitcher />
            <Link
              to={loggedIn ? "/admin" : "/admin/login"}
              data-testid="nav-admin-link"
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 hover:text-[color:var(--brand)] transition-colors border-r border-[color:var(--line)] pr-3"
            >
              <UserCircle size={16} weight="bold" />
              <span>{loggedIn ? "Dashboard" : "Admin login"}</span>
            </Link>
            <Link to={to("#contact")} data-testid="nav-cta" className="hidden sm:inline-flex btn-brand text-sm py-2.5 px-4">
              Talk to us
            </Link>
            <button
              data-testid="nav-hamburger"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 bg-[color:var(--ink)] text-white hover:bg-[color:var(--brand)] transition-colors"
            >
              <List size={18} weight="bold" />
              <span className="text-xs font-semibold uppercase tracking-wider">Menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-drawer"
        className={`fixed inset-0 z-[60] lg:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${open ? "opacity-40" : "opacity-0"}`}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white border-l border-[color:var(--line)] transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-5 h-16 border-b border-[color:var(--line)]">
            <img src="/logo-maxine.png" alt="Maxine" className="h-8 w-auto" />
            <button data-testid="drawer-close" onClick={() => setOpen(false)} className="p-2" aria-label="Close menu">
              <X size={22} />
            </button>
          </div>
          <nav className="flex flex-col p-5 gap-1">
            {NAV_LINKS.map((l) => {
              const drawerTo = l.external ? l.href : to(l.href);
              return (
                <Link
                  key={l.href}
                  to={drawerTo}
                  onClick={() => setOpen(false)}
                  data-testid={`drawer-link-${l.id}`}
                  className="py-3 border-b border-[color:var(--line)] font-display text-lg hover:text-[color:var(--brand)] transition-colors"
                >
                  {t(l.labelKey)}
                </Link>
              );
            })}
            <Link to={to("#contact")} onClick={() => setOpen(false)} data-testid="drawer-cta" className="mt-4 btn-brand justify-center">
              {t("nav.talk")}
            </Link>
            <Link to="/admin/login" onClick={() => setOpen(false)} data-testid="drawer-admin" className="mt-2 btn-ghost justify-center">
              {t("nav.admin")}
            </Link>
          </nav>
        </aside>
      </div>
    </>
  );
}
